import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Save, Info, X as XIcon, Check, Ban } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const SUB_TABS = [
  { id: "stopsell", label: "Stopsells", desc: "Freeze sales on any channel", type: "bool" },
  { id: "coa", label: "Close on Arrival", desc: "Guests cannot start a stay on these dates", type: "bool" },
  { id: "cod", label: "Close on Departure", desc: "Guests cannot end a stay on these dates", type: "bool" },
  { id: "min_nights", label: "Minimum Nights", desc: "Refuse stays shorter than this", type: "int" },
  { id: "max_nights", label: "Maximum Nights", desc: "Refuse stays longer than this", type: "int" },
];

const emptyCell = () => ({ stopsell: false, coa: false, cod: false, min_nights: 0, max_nights: 0 });

export default function RestrictionsTab({ pid, month }) {
  const [d, setD] = useState(null);
  const [cal, setCal] = useState(null);
  const [sub, setSub] = useState("stopsell");
  const [edits, setEdits] = useState({}); // { "unit_id|date": {stopsell,coa,cod,min_nights,max_nights} }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setD(null);
    Promise.all([
      api.get(`/pms/properties/${pid}/restrictions`, { params: { month } }),
      api.get(`/pms/properties/${pid}/calendar`, { params: { month } }),
    ]).then(([res, resCal]) => {
      setD(res.data); setCal(resCal.data); setEdits({});
    }).catch((e) => toast.error(errMsg(e)));
  }, [pid, month]);
  useEffect(() => { load(); }, [load]);

  const dbMap = useMemo(() => {
    const m = {};
    (d?.restrictions || []).forEach((r) => { m[`${r.unit_id}|${r.date}`] = r; });
    return m;
  }, [d]);

  const getVal = (unit_id, date, field) => {
    const k = `${unit_id}|${date}`;
    if (edits[k] && edits[k][field] !== undefined) return edits[k][field];
    return (dbMap[k] || emptyCell())[field];
  };
  const currentCell = (unit_id, date) => {
    const k = `${unit_id}|${date}`;
    return { ...emptyCell(), ...(dbMap[k] || {}), ...(edits[k] || {}) };
  };
  const setField = (unit_id, date, field, val) => {
    const k = `${unit_id}|${date}`;
    const merged = { ...currentCell(unit_id, date), [field]: val };
    setEdits((e) => ({ ...e, [k]: merged }));
  };

  const save = async () => {
    const updates = Object.entries(edits).map(([k, v]) => {
      const [unit_id, date] = k.split("|");
      return { unit_id, date, ...v };
    });
    if (!updates.length) return;
    setBusy(true);
    try {
      const { data: r } = await api.put(`/pms/properties/${pid}/restrictions`, { updates });
      toast.success(`${r.saved} restriction${r.saved > 1 ? "s" : ""} saved · pushed to every channel`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!d || !cal) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const subCfg = SUB_TABS.find((s) => s.id === sub);
  const days = cal.rates;

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-sm">Stopsells & Restrictions</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">Freeze inventory or dates on any channel in seconds. Changes push to every connected OTA on save.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-500">{Object.keys(edits).length ? `${Object.keys(edits).length} unsaved` : "All saved"}</span>
          <button data-testid="restrictions-save" onClick={save} disabled={!Object.keys(edits).length || busy}
            className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save & push
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 flex-wrap">
        {SUB_TABS.map((t) => (
          <button key={t.id} data-testid={`sub-tab-${t.id}`} onClick={() => setSub(t.id)}
            className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${sub === t.id ? "border-[#4F46E5] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-400 italic self-center flex items-center gap-1"><Info size={11} /> {subCfg.desc}</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="border-collapse w-full text-sm" style={{ minWidth: 220 + days.length * 60 }}>
          <thead>
            <tr className="bg-slate-50/60">
              <th className="sticky left-0 bg-slate-50/80 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-3 border-b border-r border-slate-200 z-10 w-56">Unit</th>
              {days.map((day) => (
                <th key={day.date} className={`text-center py-2 border-b border-r border-slate-100 ${day.weekend ? "bg-amber-50/40" : ""}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{day.weekday}</div>
                  <div className="text-[12px] font-display font-bold text-slate-800">{day.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cal.units.map((u) => (
              <tr key={u.id}>
                <td className="sticky left-0 bg-white px-4 py-2 border-b border-r border-slate-100 z-10">
                  <span className="font-display font-bold text-slate-900 text-sm">{u.name}</span>
                </td>
                {days.map((day) => {
                  const val = getVal(u.id, day.date, sub);
                  const k = `${u.id}|${day.date}`;
                  const dirty = !!edits[k];
                  if (subCfg.type === "bool") {
                    return (
                      <td key={day.date} className={`text-center border-b border-r border-slate-100 p-0.5 ${day.weekend ? "bg-amber-50/20" : ""} ${dirty ? "bg-[#EEF2FF]/50" : ""}`}>
                        <button data-testid={`cell-${sub}-${u.id}-${day.date}`}
                          onClick={() => setField(u.id, day.date, sub, !val)}
                          className={`w-full py-2 flex items-center justify-center transition-colors rounded ${val ? "bg-red-500/90 hover:bg-red-600 text-white" : "hover:bg-slate-100 text-slate-300"}`}
                          title={val ? `Click to remove ${subCfg.label}` : `Click to apply ${subCfg.label}`}>
                          {val ? <Ban size={14} /> : <span className="text-[10px] font-semibold">—</span>}
                        </button>
                      </td>
                    );
                  }
                  return (
                    <td key={day.date} className={`text-center border-b border-r border-slate-100 p-1 ${day.weekend ? "bg-amber-50/20" : ""} ${dirty ? "bg-[#EEF2FF]/50" : ""}`}>
                      <input data-testid={`cell-${sub}-${u.id}-${day.date}`}
                        type="number" min="0" max="60"
                        value={val || ""}
                        onChange={(e) => setField(u.id, day.date, sub, parseInt(e.target.value || "0", 10))}
                        placeholder="—"
                        className={`w-full max-w-[54px] text-center border rounded px-1 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#4F46E5] ${val > 0 ? "border-[#4F46E5] text-[#4F46E5] font-bold" : "border-transparent text-slate-500"}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Stopsells, COA and COD immediately refuse new reservations — the same Double Booking Guard that already handles overlaps also enforces every restriction on this page.</p>
    </div>
  );
}
